/**
 * Lightweight, dependency-free SEO + JSON-LD injector for client-side routes.
 *
 * Manages:
 *  - document.title
 *  - <meta name="description">
 *  - canonical <link>
 *  - Open Graph (title, description, image, type, url)
 *  - Twitter card
 *  - One JSON-LD <script type="application/ld+json"> block
 *
 * All tags created by this hook are tagged with [data-l2m-seo] so they can be
 * safely removed when the route unmounts, without touching any tags rendered
 * by index.html.
 */
import { useEffect } from "react";

const ATTR = "data-l2m-seo";

function setMeta(selector, value, createAttrs) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(createAttrs.tag || "meta");
    Object.entries(createAttrs.attrs || {}).forEach(([k, v]) => el.setAttribute(k, v));
    el.setAttribute(ATTR, "true");
    document.head.appendChild(el);
  }
  if (createAttrs.attrs?.rel === "canonical") {
    el.setAttribute("href", value);
  } else {
    el.setAttribute("content", value);
  }
}

function setJsonLd(payload) {
  // Remove any previous JSON-LD added by this hook.
  document.head
    .querySelectorAll(`script[type="application/ld+json"][${ATTR}]`)
    .forEach((n) => n.remove());
  if (!payload) return;
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute(ATTR, "true");
  script.text = JSON.stringify(payload);
  document.head.appendChild(script);
}

const DEFAULT_OG_IMAGE = "https://linked2marketing.com/og.jpg";

export default function useSeo({
  title,
  description,
  canonical,
  ogImage,
  ogType = "article",
  jsonLd,
} = {}) {
  const finalOgImage = ogImage || DEFAULT_OG_IMAGE;
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    if (description) {
      setMeta('meta[name="description"]', description, {
        attrs: { name: "description" },
      });
      setMeta('meta[property="og:description"]', description, {
        attrs: { property: "og:description" },
      });
      setMeta('meta[name="twitter:description"]', description, {
        attrs: { name: "twitter:description" },
      });
    }
    if (title) {
      setMeta('meta[property="og:title"]', title, {
        attrs: { property: "og:title" },
      });
      setMeta('meta[name="twitter:title"]', title, {
        attrs: { name: "twitter:title" },
      });
    }
    if (canonical) {
      setMeta('link[rel="canonical"]', canonical, {
        tag: "link",
        attrs: { rel: "canonical" },
      });
      setMeta('meta[property="og:url"]', canonical, {
        attrs: { property: "og:url" },
      });
    }
    if (finalOgImage) {
      setMeta('meta[property="og:image"]', finalOgImage, {
        attrs: { property: "og:image" },
      });
      setMeta('meta[name="twitter:image"]', finalOgImage, {
        attrs: { name: "twitter:image" },
      });
    }
    setMeta('meta[property="og:type"]', ogType, {
      attrs: { property: "og:type" },
    });
    setMeta('meta[name="twitter:card"]', "summary_large_image", {
      attrs: { name: "twitter:card" },
    });

    setJsonLd(jsonLd);

    return () => {
      // Reset only the dynamic tags this hook owns; leave the static ones
      // injected by index.html alone.
      document.head
        .querySelectorAll(`[${ATTR}]`)
        .forEach((n) => n.remove());
      document.title = prevTitle;
    };
  }, [title, description, canonical, finalOgImage, ogType, JSON.stringify(jsonLd)]);
}
